import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { fmtErr, money, fdt } from "../../api";
import { Stat } from "../../components/bits";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Plus, Trash2, TicketPercent } from "lucide-react";

const inputCls = "cr-input !text-xs !py-2 !rounded-xl";
const btnBlack = "bg-[#1C1B1A] text-white text-xs font-medium px-4 py-2 rounded-full hover:bg-[#2C2B2A] transition-all";
const btnOutline = "border border-[#E8E4D9] text-xs font-medium px-3 py-1.5 rounded-full hover:bg-[#FCFAF5] transition-colors";

export function PromosTab() {
  const [promos, setPromos] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", kind: "percent", value: "", months: "", currency: "GBP", max_uses: "", expires_at: "" });

  const load = useCallback(() => {
    api.get("/admin/promos").then((r) => setPromos(r.data.promos)).catch(() => {});
    api.get("/admin/promos/redemptions").then((r) => setRedemptions(r.data.redemptions)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const create = async () => {
    try {
      await api.post("/admin/promos", {
        code: form.code, kind: form.kind, value: form.kind === "free_months" ? null : Number(form.value),
        months: form.kind === "free_months" ? Number(form.months) : null,
        currency: form.currency, max_uses: form.max_uses ? Number(form.max_uses) : null,
        expires_at: form.expires_at || null,
      });
      toast.success("Promo code created");
      setOpen(false);
      setForm({ code: "", kind: "percent", value: "", months: "", currency: "GBP", max_uses: "", expires_at: "" });
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="space-y-4" data-testid="ct-promos">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[#78756E]">{promos.length} codes · {redemptions.filter((r) => r.status === "redeemed").length} redeemed</p>
        <button data-testid="promo-create-open" onClick={() => setOpen(true)} className={`${btnBlack} flex items-center gap-1.5`}>
          <Plus className="w-3.5 h-3.5" /> New code
        </button>
      </div>

      <div className="cr-card !p-0 cr-table-wrap">
        <table className="w-full text-xs">
          <thead><tr className="text-left text-[#A09D95] border-b border-[#E8E4D9]">
            <th className="p-3 font-semibold">Code</th><th className="p-3 font-semibold">Discount</th>
            <th className="p-3 font-semibold">Usage</th><th className="p-3 font-semibold">Expires</th>
            <th className="p-3 font-semibold">Status</th><th className="p-3 font-semibold">Actions</th>
          </tr></thead>
          <tbody>
            {promos.map((p) => (
              <tr key={p.id} className="border-b border-[#F6F3EA] last:border-0" data-testid={`promo-row-${p.code}`}>
                <td className="p-3 font-mono font-bold flex items-center gap-1.5"><TicketPercent className="w-3.5 h-3.5 text-[#A09D95]" />{p.code}</td>
                <td className="p-3">{p.kind === "free_months" ? `${p.months} months free` : p.kind === "percent" ? `${p.value}%` : money(p.value, p.currency)}</td>
                <td className="p-3">{p.used_count}{p.max_uses != null ? ` / ${p.max_uses}` : " used"}</td>
                <td className="p-3 text-[#78756E]">{p.expires_at ? p.expires_at.slice(0, 10) : "Never"}</td>
                <td className="p-3">
                  <button data-testid={`promo-toggle-${p.code}`} onClick={async () => { await api.patch(`/admin/promos/${p.id}`, { active: !p.active }); load(); }}
                    className={p.active ? "cr-pill-yellow" : "cr-pill-outline"}>{p.active ? "Active" : "Off"}</button>
                </td>
                <td className="p-3">
                  <button data-testid={`promo-delete-${p.code}`} onClick={async () => {
                    if (!window.confirm(`Delete code ${p.code}?`)) return;
                    await api.delete(`/admin/promos/${p.id}`); toast.success("Deleted"); load();
                  }} className="text-[#A09D95] hover:text-red-600 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}
            {promos.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-[#A09D95]">No promo codes yet — create your first one.</td></tr>}
          </tbody>
        </table>
      </div>

      <section className="cr-card !p-4" data-testid="promo-redemptions">
        <h3 className="text-xs font-bold mb-2">Recent redemptions</h3>
        {redemptions.length === 0 && <p className="text-[11px] text-[#A09D95]">None yet.</p>}
        {redemptions.slice(0, 15).map((r) => (
          <div key={r.id} className="flex justify-between text-xs py-1.5 border-b border-[#F6F3EA] last:border-0">
            <span><span className="font-mono font-semibold">{r.code}</span> · {r.user_email} · {r.plan_name}</span>
            <span className={r.status === "redeemed" ? "font-semibold" : "text-[#A09D95]"}>
              −{money(r.discount, r.currency)} · {r.status}
            </span>
          </div>
        ))}
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="promo-create-dialog">
          <DialogHeader><DialogTitle>New promo code</DialogTitle></DialogHeader>
          <div className="space-y-2 mt-2">
            <input data-testid="promo-code" placeholder="CODE (e.g. LAUNCH20)" value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className={`${inputCls} w-full font-mono`} />
            <div className="flex gap-2">
              <select data-testid="promo-kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className={inputCls}>
                <option value="percent">Percent %</option><option value="fixed">Fixed amount</option><option value="free_months">Free months</option>
              </select>
              {form.kind === "free_months" ? (
                <input data-testid="promo-months" type="number" min="1" max="24" placeholder="Months free (e.g. 3)" value={form.months}
                  onChange={(e) => setForm({ ...form, months: e.target.value })} className={`${inputCls} w-full`} />
              ) : (
                <input data-testid="promo-value" type="number" min="0" step="0.01" placeholder={form.kind === "percent" ? "%" : "Amount"} value={form.value}
                  onChange={(e) => setForm({ ...form, value: e.target.value })} className={`${inputCls} w-full`} />
              )}
              {form.kind === "fixed" && (
                <select data-testid="promo-currency" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className={inputCls}>
                  <option>EUR</option><option>GBP</option>
                </select>
              )}
            </div>
            <div className="flex gap-2">
              <input data-testid="promo-max-uses" type="number" min="1" placeholder="Max uses (blank = unlimited)" value={form.max_uses}
                onChange={(e) => setForm({ ...form, max_uses: e.target.value })} className={`${inputCls} w-full`} />
              <input data-testid="promo-expires" type="date" value={form.expires_at}
                onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className={`${inputCls} w-full`} />
            </div>
            <button data-testid="promo-create-btn" onClick={create} disabled={!form.code || (form.kind === "free_months" ? !form.months : !form.value)}
              className={`${btnBlack} w-full !py-2.5 disabled:opacity-40`}>Create code</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function FinancesTab() {
  const [data, setData] = useState(null);
    useEffect(() => { api.get("/admin/finances").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <p className="text-xs text-[#A09D95] py-10 text-center">Loading…</p>;

  return (
    <div className="space-y-4" data-testid="ct-finances">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {data.totals.map((t) => (
          <React.Fragment key={t.currency}>
            <Stat label={`Gross ${t.currency}`} value={money(t.gross, t.currency)} sub={`${t.payments} payments`} />
            <Stat label={`Net ${t.currency}`} value={money(t.net, t.currency)}
              sub={`−${money(t.refunded, t.currency)} refunds · −${money(t.discounts, t.currency)} promo`} />
          </React.Fragment>
        ))}
        {data.totals.length === 0 && <Stat label="Revenue" value="—" sub="No payments yet" />}
        <Stat label="Promo codes" value={data.promo_count} sub={`${data.redeemed_count} redeemed`} />
      </div>

      <div className="cr-card !p-0 cr-table-wrap" data-testid="finances-payments">
        <table className="w-full text-xs">
          <thead><tr className="text-left text-[#A09D95] border-b border-[#E8E4D9]">
            <th className="p-3 font-semibold">Date</th><th className="p-3 font-semibold">User</th>
            <th className="p-3 font-semibold">Plan</th><th className="p-3 font-semibold">Amount</th>
            <th className="p-3 font-semibold">Promo</th><th className="p-3 font-semibold">Status</th>
          </tr></thead>
          <tbody>
            {data.payments.map((p) => (
              <tr key={p.id} className="border-b border-[#F6F3EA] last:border-0">
                <td className="p-3 text-[#78756E]">{fdt(p.created_at)}</td>
                <td className="p-3 font-mono text-[11px]">{p.user_id?.slice(0, 8)}</td>
                <td className="p-3">{p.plan_name} · {p.interval}</td>
                <td className="p-3 font-semibold">{money(p.amount, (p.currency || "eur").toUpperCase())}</td>
                <td className="p-3">{p.promo_code ? <span className="font-mono">{p.promo_code}</span> : "—"}</td>
                <td className="p-3">
                  <span className={p.payment_status === "paid" ? "cr-pill-yellow" : p.payment_status === "refunded" ? "cr-pill-dark" : "cr-pill-outline"}>
                    {p.payment_status}
                  </span>
                </td>
              </tr>
            ))}
            {data.payments.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-[#A09D95]">No payments yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <section className="cr-card !p-4" data-testid="finances-reductions">
        <h3 className="text-xs font-bold mb-2">Promo reductions (redeemed)</h3>
        {data.redemptions.length === 0 && <p className="text-[11px] text-[#A09D95]">No reductions yet.</p>}
        {data.redemptions.slice(0, 20).map((r) => (
          <div key={r.id} className="flex justify-between text-xs py-1.5 border-b border-[#F6F3EA] last:border-0">
            <span><span className="font-mono font-semibold">{r.code}</span> · {r.user_email} · {r.plan_name} · {fdt(r.at)}</span>
            <span className="font-semibold">−{money(r.discount, r.currency)} <span className="text-[#A09D95] font-normal">({money(r.amount_before, r.currency)} → {money(r.amount_after, r.currency)})</span></span>
          </div>
        ))}
      </section>
    </div>
  );
}
